import { ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export const SPECIAL_ROLES = ["super_admin", "admin"];

export function formatTime(dateInput: string | Date | null): string {
  // Handle null or undefined input by returning a default or error message
  if (!dateInput) {
    return "Invalid Date"; // You can return a default message or handle the error as you see fit
  }

  const date = new Date(dateInput); // Convert the string or Date to a Date object

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date"; // Handle invalid date formats
  }

  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };

  return new Intl.DateTimeFormat("en-US", options)
    .format(date)
    .replace(",", "");
}

export function formatTimeDate(dateInput: string | Date | null): string {
  // Handle null or undefined input by returning a default or error message
  if (!dateInput) {
    return "Invalid Date"; // You can return a default message or handle the error as you see fit
  }

  const date = new Date(dateInput); // Convert the string or Date to a Date object
  const day = date.getUTCDate();
  const year = date.getUTCFullYear();
  const month = date.toLocaleString("default", {
    month: "long",
    timeZone: "UTC",
  });

  // Helper to get the ordinal suffix (st, nd, rd, th)
  const getOrdinalSuffix = (n: number): string => {
    if (n >= 11 && n <= 13) return "th";
    switch (n % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  // Check if the date is valid
  if (isNaN(date.getTime())) {
    return "Invalid Date"; // Handle invalid date formats
  }

  const suffix = getOrdinalSuffix(day);
  return `${month} ${day}${suffix}, ${year}`;
}

export function formatToLocalTime(dateTimeStr: string): string {
  // Parse the input datetime string (assuming it's in UTC)
  const date = new Date(dateTimeStr.replace(" ", "T") + "Z");

  // Format options - will automatically use user's local timezone
  const dateOptions: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
  };

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  };

  // Get formatted date and time in user's timezone
  const formattedDate = date.toLocaleDateString("en-GB", dateOptions);
  const formattedTime = date
    .toLocaleTimeString("en-US", timeOptions)
    .toLowerCase();

  return `${formattedDate}, ${formattedTime}`;
}

export function removeEmptySpaces(input: string): string {
  return input?.replace(/\s+/g, "");
}

export const reactSelectStyleTable = {
  control: (base: any, state: any) => ({
    ...base,
    border: state.isFocused
      ? "0.1rem solid rgba(115, 71, 146, 1)"
      : "0.1rem solid rgba(115, 71, 146, 1)",
    // backgroundColor: state.isSelected ? "rgba(115, 71, 146, 1)" : "white",
    boxShadow: state.isFocused
      ? "0.1rem solid rgba(115, 71, 146, 1)"
      : "0.1rem solid rgba(115, 71, 146, 1)",
    "&:hover": {
      // border: state.isFocused ? 0 : 0
    },
  }),
  option: (provided: any, state: any) => ({
    ...provided,
    backgroundColor: state.isSelected ? "rgba(115, 71, 146, 1)" : "white",
    color: state.isSelected ? "#ffffff" : "",
  }),
};

export function capitalizeFirstLetter(input: string): string {
  if (String(input).length === 0) return String(input); // Return if string is empty
  return (
    String(input).charAt(0).toUpperCase() + String(input).slice(1).toLowerCase()
  );
}

export function addZeroToNumber(param: string | number) {
  if (param) {
    const val = Number(param) < 10 ? `0${param}` : param;
    return val;
  } else {
    return "00";
  }
}

type Permission = {
  name: string;
  value: boolean;
};

type Category = {
  category_name: string;
  permissions: Permission[];
};

export function getTruePermissions(categories: Category[]): string[] {
  return categories.flatMap(
    (category) =>
      category.permissions
        .filter((permission) => permission.value) // Filter permissions with value === true
        .map((permission) => permission.name), // Extract the name of the permission
  );
}

export function arraysHaveDifferences(arr1: string[], arr2: string[]): boolean {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  // Convert sets to arrays before iterating
  for (let value of Array.from(set1)) {
    if (!set2.has(value)) return true;
  }

  for (let value of Array.from(set2)) {
    if (!set1.has(value)) return true;
  }

  return false;
}

/**
 * Converts a JavaScript object into FormData.
 * @param obj - The object to convert.
 * @param formData - An optional FormData instance to append to.
 * @param parentKey - An optional parent key for nested objects.
 * @returns A FormData object containing the converted data.
 */
export function objectToFormData(
  obj: Record<string, any>,
  formData: FormData = new FormData(),
  parentKey: string = "",
): FormData {
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];
      const formKey = parentKey ? `${parentKey}[${key}]` : key;

      if (value instanceof Date) {
        // Append Date objects as ISO strings
        formData.append(formKey, value.toISOString());
      } else if (value instanceof File || value instanceof Blob) {
        // Append File or Blob objects
        formData.append(formKey, value);
      } else if (Array.isArray(value)) {
        // Handle arrays
        value.forEach((arrayValue, index) => {
          const arrayKey = `${formKey}[${index}]`;
          if (typeof arrayValue === "object" && !(arrayValue instanceof File)) {
            objectToFormData(arrayValue, formData, arrayKey);
          } else {
            formData.append(arrayKey, arrayValue);
          }
        });
      } else if (typeof value === "object" && value !== null) {
        // Recursively handle nested objects
        objectToFormData(value, formData, formKey);
      } else if (value !== null && value !== undefined) {
        // Append primitive values
        formData.append(formKey, value);
      }
    }
  }
  return formData;
}

export const AddAllToList = (list: any, text?: string) => {
  if (list?.length > 0) {
    const addAllList = [
      ...list,
      { label: `All ${text || ""}`, value: "", name: "" },
    ];
    return addAllList;
  }
};

interface Currency {
  id: string;
  code?: string;
  label?: string;
  [key: string]: any; // Allow additional properties
}

/**
 * Finds the object and its `id` based on a provided `code` or `label`.
 * @param array - The array of currency objects.
 * @param value - The `code` or `label` value to search for.
 * @returns An object containing the matched object and its `id`, or null if not found.
 */
export function findCurrencyByCodeOrLabel(
  array: Currency[],
  value: string,
): { obj: Currency; id: string } | null {
  const result = array.find(
    (item) => item.code === value || item.label === value,
  );

  if (result) {
    return { obj: result, id: result.id };
  }
  return null;
}

/**
 * Formats a Date object into "YYYY-MM-DD" format.
 * @param date - The date to format (can be a Date object, string, or number).
 * @returns A string representing the formatted date.
 */
export function formatDateToYYYYMMDD(date: Date | string | number): string {
  const d = new Date(date); // Convert input to Date object
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0"); // Add leading zero for single-digit months
  const day = String(d.getDate()).padStart(2, "0"); // Add leading zero for single-digit days

  return `${year}-${month}-${day}`;
}

type DataObject = {
  [key: string]: any; // Represents any key-value pairs in the object
};

export const processData = (
  data: (string | number | null)[],
): (string | number | null)[] => {
  return data
    .map((val, idx, arr) => {
      const prev = arr[idx - 1] ?? null; // Previous value
      const next = arr[idx + 1] ?? null; // Next value

      // If the current value is 0 and both previous and next are 0, return "null"
      if (val === 0 && prev === 0 && next === 0) {
        return "null";
      }

      // Otherwise, return the value (if it's 0 but not surrounded by 0's, or any other value)
      return val;
    })
    .map((val) => (val === "null" ? null : val)); // Convert "null" strings to actual null values for the chart
};

export function getArrayOfValues(
  key: string,
  data: DataObject[],
  returnAsNumber: boolean = false,
  fullLabel: boolean = false,
): (string | number | null)[] {
  return data.map((item) => {
    const value = item[key] ?? null;

    if (key === "label" && typeof value === "string") {
      // Trim label value to 3 characters
      return fullLabel ? capitalizeFirstLetter(value) : value.substring(0, 3);
    }

    if (returnAsNumber) {
      const numValue = Number(value);
      return isNaN(numValue) ? null : numValue; // Return null for invalid number values
      // return isNaN(numValue) ? null : String(numValue)?.replace("-", ''); // Return null for invalid number values
    }

    return value;
  });
}

export function getArrayOfValuesTwo(
  key: string,
  data: DataObject[],
): (string | number)[] {
  return data.map((item) => item[key] ?? null); // Return the value or `null` if the key doesn't exist
}

export function rearrangeObjectsByKey<T>(
  key: keyof T,
  value: T[keyof T],
  array: T[],
): T[] {
  // Separate objects matching the value from the rest
  const matching = array.filter((item) => item[key] === value);
  const nonMatching = array.filter((item) => item[key] !== value);

  // Combine matching objects first, followed by the rest
  return [...matching, ...nonMatching];
}

export function hasSpecialCharacters(input: string): boolean {
  // Regular expression to match any special characters
  const specialCharRegex = /[!@#$%^&*(),.?":{}|<>_\-+=/[\]\\~`]/;

  return specialCharRegex.test(input);
}

export function hasSpecialCharactersOrNumbers(input: string): boolean {
  // Regular expression to match any special characters or digits (0-9)
  const regex = /[0-9!@#$%^&*(),.?":{}|<>_\-+=/[\]\\~`]/;

  return regex.test(input);
}

export function approximateToNearestWholeNumber(value: number): number {
  return Math.round(value);
}

// export const changeCustomersToUSers = (param?: string) => {
//   if (param) {
//     const val = param?.replaceAll("customer", "user");
//     return val;
//   }
// };

// // no longer needed, BE has removed the customerSlice, user to userEvent, team member``
// export const changeUsersToTeamMember = (param?: string) => {
//   if (param) {
//     const val = param?.replaceAll("users", "team member");
//     const valTwo = val?.replaceAll("user", "team member");
//     // const valThree = val?.replaceAll("User", "team member");
//     return valTwo;
//   }
// };

// Export/Download an image with a src (url)
export const downloadImageSrc = async (
  imageSrc: string,
  fileName = "downloaded-image.jpg",
) => {
  try {
    const response = await fetch(imageSrc, { mode: "cors" }); // Ensures cross-origin access
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url); // Cleanup URL
  } catch (error) {
    console.error("Error downloading the image:", error);
  }
};

export const formatBytes = (bytes: number, decimals = 2): string => {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${(bytes / Math.pow(k, i)).toFixed(decimals)} ${sizes[i]}`;
};

export const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      if (reader.result) {
        resolve(reader.result as string);
      } else {
        reject(new Error("FileReader result is null"));
      }
    };
    reader.onerror = (error) => reject(error);
  });
};

export const getFileExtension = (url: string) => {
  try {
    const cleanUrl = url.split("?")[0]; // remove query params if any
    const parts = cleanUrl.split(".");
    return parts.length > 1 ? (parts.pop()?.toLowerCase() ?? "") : null;
  } catch (error) {
    console.error("Invalid URL:", error);
    return "";
  }
};

export const handleFileDownload = async (url: string, filename?: string) => {
  try {
    // Fetch the file as a Blob
    const response = await fetch(url);
    const blob = await response.blob();

    // Create a temporary download link
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename || "download";

    // Trigger the download
    document.body.appendChild(link);
    link.click();

    // Cleanup
    link.remove();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Download failed:", error);
  }
};

export function detectCloudinaryFileType(url: string) {
  try {
    const extensionMatch = url.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    const extension = extensionMatch ? extensionMatch[1].toLowerCase() : "";

    if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
      return "image";
    }
    if (extension === "pdf") return "pdf";
    if (["mp4", "mov", "avi", "webm"].includes(extension)) return "video";
    if (["zip", "docx", "xlsx", "csv", "txt"].includes(extension)) return "raw";

    // fallback based on path (resource_type)
    const match = url.match(/\/(image|video|raw)\//);
    return match ? match[1] : "unknown";
  } catch (error) {
    console.error("Invalid URL:", error);
    return "unknown";
  }
}

/**
 * URL to open a document in a new tab for previewing. PDFs are opened
 * directly since every browser renders them natively and consistently
 * across OSes. Everything else (docx, xlsx, csv, etc.) can't be rendered
 * inline by the browser, so it's routed through Google Docs Viewer instead
 * of triggering a raw file download — a downloaded file with no recognized
 * extension/content-type fails to open on Windows even though it may still
 * work on Mac.
 */
export function getDocumentPreviewUrl(fileUrl: string, isPdf: boolean): string {
  if (!fileUrl) return fileUrl;
  if (isPdf) return fileUrl;
  return `https://docs.google.com/gview?url=${encodeURIComponent(fileUrl)}&embedded=false`;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Plain-text length/emptiness of rich-text (HTML) content — e.g. a Quill
 * editor emits "<p><br></p>" for an empty field, so checking the raw string
 * length or truthiness isn't enough to tell if the user has actually typed
 * anything.
 */
export function stripHtml(html: string): string {
  if (!html) return "";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
}

/**
 * Converts plain text (e.g. from a textarea) into HTML safe to hand to a
 * rich-text editor as its initial value — escapes HTML-sensitive characters
 * so the text renders literally rather than being misread as markup, and
 * turns line breaks into <br> since plain newlines are ignored in HTML.
 */
export function plainTextToHtml(text: string): string {
  if (!text) return "";
  const div = document.createElement("div");
  return text
    .split("\n")
    .map((line) => {
      div.textContent = line;
      return div.innerHTML;
    })
    .join("<br>");
}
